import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Focus shell - moves keyboard focus into the runner when a fresh
 * session starts. Without this, a user who clicked a smart-start card
 * is still tab-focused on the (now-unmounted) Setup button; the very
 * next keystroke (Space to reveal a flashcard, 1-4 to rate) targets
 * whatever React fell back to, usually `<body>`, and silently
 * no-ops. Focusing the runner container makes the runner's
 * keyboard handlers reachable immediately.
 *
 * Wrapped in a separate component (instead of inline useEffect on
 * the orchestrator) so it remounts naturally with the runner key -
 * one focus event per session start, never on re-renders within
 * the same session.
 */
export function RunnerFocusShell({
  sessionId: _sessionId,
  children,
}: {
  sessionId: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Defer one frame so all child effects (timer init, query
    // subscribe) settle before we grab focus. Otherwise the runner's
    // own initial focus call (e.g. autofocus on the first option)
    // would clobber ours mid-frame.
    const raf = window.requestAnimationFrame(() => {
      ref.current?.focus?.({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(raf);
  }, []);
  return (
    <div ref={ref} tabIndex={-1} className='flex h-full min-h-0 flex-col'>
      {children}
    </div>
  );
}
